import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ComponentRef,
  Injectable,
  Renderer2,
  RendererFactory2,
  createComponent,
  inject,
} from '@angular/core';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { ANIMATION_DURATION } from '../../../../design-system/design-system.constants';
import { CloseDialogData, DialogConfig } from './dialog.model';

const escapeExceptions = ['.options-dropdown', '.ag-popup', '.cv-dialog-content-users-list', 'app-sidebar>aside'];

type DialogInstance<T> = {
  componentReference: ComponentRef<T>;
  dialogElement: HTMLDialogElement;
  close$: Subject<CloseDialogData<T>>;
  closeDialogDataSubscription: Subscription;
};

type QueueItem<T> = {
  component: T;
  config: DialogConfig<T> | undefined;
  closeSubject: Subject<CloseDialogData<T>>;
};

@Injectable({ providedIn: 'root' })
export class DialogService {
  private _applicationReference = inject(ApplicationRef);
  private _document = inject(DOCUMENT);
  private _rendererFactory = inject(RendererFactory2);

  isOpen: boolean = false;
  private _dialogs: DialogInstance<any>[] = [];
  private _renderer: Renderer2 = this._rendererFactory.createRenderer(null, null);
  private _queue: QueueItem<any>[] = [];

  constructor() {
    this._document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this._dialogs.length > 0) {
          event.preventDefault();

          const hasEscapeException = !!this._dialogs.at(-1)?.dialogElement?.querySelector(escapeExceptions.join(', '));
          if (hasEscapeException) {
            return;
          }

          const componentInstance = this._dialogs.at(-1)?.componentReference.instance;
          const hasInvalidSettingsStatus = componentInstance?.status?.() === 'INVALID';
          if (hasInvalidSettingsStatus) {
            componentInstance?.considerLeaveUnsavedChanges?.('close');
            return;
          }

          this._close();
        }
      },
      true
    );
  }

  /**
   * Closes all open dialogs and clears the queue
   */
  closeAll(): void {
    this._queue = [];
    this._dialogs.forEach((dialog) => this._close(undefined, dialog.dialogElement));
  }

  /**
   * @param component Component to attach inside the dialog element
   * @param dialogConfig Config to pass to the dialog component
   * @returns Observable that emits when the dialog is closed
   */
  open<T>(component: T, dialogConfig?: DialogConfig<T>): Observable<CloseDialogData<T>> {
    const close$ = new Subject<CloseDialogData<T>>();

    if (dialogConfig?.stack) {
      // Stack the dialog on top of existing dialogs
      this._openDialog(component, dialogConfig, close$);
    } else {
      // If there is already a dialog open, add the new dialog to the queue
      if (this._dialogs.length > 0) {
        this._queue.push({ component, config: dialogConfig, closeSubject: close$ as any });
        return close$.asObservable();
      }

      this._openDialog(component, dialogConfig, close$);
    }

    return close$.asObservable();
  }

  private _close<T>(closeDialogData?: CloseDialogData<T>, dialogElementToClose?: HTMLDialogElement): void {
    // Find the dialog instance to close
    const dialogInstance: DialogInstance<any> | undefined = dialogElementToClose
      ? this._dialogs.find((d) => d.dialogElement === dialogElementToClose)
      : this._dialogs.at(-1);

    if (!dialogInstance) {
      return;
    }

    dialogInstance.close$.next(closeDialogData);
    dialogInstance.close$.complete();
    dialogInstance.dialogElement.open = false;

    timer(ANIMATION_DURATION).subscribe(() => {
      this._destroyDialogInstance(dialogInstance); // Await closing animation to end before destroying the dialog
      this._dialogs = this._dialogs.filter((d) => d !== dialogInstance);
      this.isOpen = this._dialogs.length > 0;

      if (this._queue.length > 0 && this._dialogs.length === 0) {
        const nextDialog = this._queue.shift(); // Get the next dialog in the queue
        if (nextDialog) {
          this._openDialog(nextDialog.component, nextDialog.config, nextDialog.closeSubject);
        }
      }
    });
  }

  private _createComponent = createComponent; // The reason for attaching this function as a method on the service and not calling it directly is to be able to mock it in the tests

  private _destroyDialogInstance(dialogInstance: DialogInstance<any>) {
    if (dialogInstance.closeDialogDataSubscription) {
      dialogInstance.closeDialogDataSubscription.unsubscribe();
    }
    if (dialogInstance.componentReference) {
      this._applicationReference.detachView(dialogInstance.componentReference.hostView);
      dialogInstance.componentReference.destroy();
    }
    if (dialogInstance.dialogElement) {
      dialogInstance.dialogElement.remove();
    }
  }

  private _openDialog<T>(
    component: T,
    dialogConfig: DialogConfig<T> | undefined,
    closeSubject: Subject<CloseDialogData<T>>
  ): void {
    // Create dialog element
    const dialogElement = this._renderer.createElement('dialog');
    dialogElement.classList.add(dialogConfig?.class ?? 'cv-dialog');

    // Create component and append to dialog element
    const componentReference: ComponentRef<any> = this._createComponent(component as any, {
      environmentInjector: this._applicationReference.injector,
      hostElement: dialogElement,
    });

    // Set data from the caller to the dialog component input and
    // set up listener for close event from the dialog component
    componentReference.setInput('openDialogData', dialogConfig?.data);
    const closeDialogDataSubscription = (<Observable<CloseDialogData<T>>>(
      componentReference.instance.closeDialogData
    )).subscribe((closeDialogData) => {
      this._close<T>(<CloseDialogData<T> | undefined>closeDialogData, dialogElement);
    });

    // Attach the component to the application and append the dialog to the document body
    this._applicationReference.attachView(componentReference.hostView);
    this._renderer.appendChild(this._document.body, dialogElement);

    // Open the dialog
    dialogConfig?.nonModal ? dialogElement.show() : dialogElement.showModal();

    // Store the dialog instance
    const dialogInstance: DialogInstance<T> = {
      componentReference,
      dialogElement,
      close$: closeSubject,
      closeDialogDataSubscription,
    };
    this._dialogs.push(dialogInstance);
    this.isOpen = true;
  }
}
